/**
 * The game page's hero (docs/contest-ux.md §1–3, slimmed in Revision 1 to one row): both teams
 * with logos (or the event name for futures), league · venues, the start time or live clock +
 * score, one chip for signals / best edge, and the freshness dot. On mobile it collapses to a one-line bar on scroll
 * (`CompactHero`, rendered by the page inside its sticky header).
 */
import type { Contest, ContestState, League } from "@greenbook/client";
import { fmtKickoff, fmtRelative } from "../../lib/format.js";
import { isGame, matchupSep, teamLabel, teamsOf, type Team } from "../../lib/marketsBoard.js";
import { SCOPES, useAccess } from "../../lib/omauth.js";
import { VenueLogo } from "../VenueLogo.js";
import { SoftCard, Skeleton } from "../kit.js";
import { showUnlock } from "../Gate.js";
import { SignInButton } from "../SignInWithOpenMarkets.js";

/* ------------------------------------------------------------------ game state */

export interface HeroState {
  live: boolean;
  final: boolean;
  clock: string | null;
  period: string | null;
  home: number | null;
  away: number | null;
}

/** What the hero reads from contest state (null when there's no state to read). */
export function heroState(state: ContestState | null | undefined): HeroState | null {
  if (!state) return null;
  const gs = state.state;
  const s = (gs?.game_status ?? "").toLowerCase();
  const final = s.includes("final") || s.includes("complete") || s.includes("settled");
  const pre = s === "" || s.includes("pre") || s.includes("scheduled") || s.includes("pending");
  const scores = gs?.scores as Record<string, number | null | undefined> | null | undefined;
  return {
    live: state.live === true || (!pre && !final && s.length > 0),
    final,
    clock: gs?.clock?.display ?? null,
    period: gs?.period?.label ?? null,
    // Live sends {home_score, away_score}; the docs say {home, away}. Read either.
    home: scores?.home ?? scores?.home_score ?? null,
    away: scores?.away ?? scores?.away_score ?? null,
  };
}

const hasScore = (s: HeroState | null) => !!s && (s.home != null || s.away != null);

function startLine(contest: Contest | undefined): string {
  if (!contest?.starts_at) return "";
  const t = Date.parse(contest.starts_at);
  const rel = fmtRelative(contest.starts_at);
  return Number.isFinite(t) && t > Date.now() ? `${fmtKickoff(contest.starts_at)} · ${rel}` : `Started ${rel}`;
}

/* ------------------------------------------------------------------ freshness */

/** A small dot with a tooltip ("prices 12s old"), amber when the feed may be stale. */
export function FreshnessDot({ ageSec, wsDown }: { ageSec: number | null; wsDown: boolean }) {
  const stale = (ageSec != null && ageSec > 60) || (wsDown && (ageSec == null || ageSec > 30));
  const age = ageSec == null ? "prices loading" : `prices ${ageSec < 90 ? `${ageSec}s` : `${Math.round(ageSec / 60)}m`} old`;
  const text = `${age}${wsDown ? " · live feed reconnecting, refreshing every 30s" : stale ? " · may be stale" : " · updating live"}`;
  return (
    <span className="relative inline-flex h-5 w-5 items-center justify-center" tabIndex={0} aria-label={text} title={text}>
      <span className={`inline-block h-2 w-2 rounded-full ${stale ? "bg-amber" : "bg-up"}`} aria-hidden />
    </span>
  );
}

/* ---------------------------------------------------------------- signals chip */

export interface HeroChip {
  text: string;
  onClick: () => void;
}

/* ------------------------------------------------------------------- the hero */

/** One team in the slim hero: logo, name, and the score when there is one. */
function TeamChip({
  team,
  logo,
  score,
  bold,
}: {
  team: Team;
  logo?: string | null;
  score: number | null | undefined;
  bold: boolean;
}) {
  return (
    <span className="inline-flex min-w-0 items-center gap-2">
      <VenueLogo name={team.name ?? team.abbr} logoUrl={logo} size={26} />
      <span className="min-w-0 break-words text-[16px] font-semibold leading-snug text-ink lg:text-[17px]">{teamLabel(team)}</span>
      {score != null && (
        <span className={`nums shrink-0 text-[18px] font-semibold ${bold ? "text-ink" : "text-dim"}`}>{score}</span>
      )}
    </span>
  );
}

function StatusLine({ contest, st, game }: { contest: Contest | undefined; st: HeroState | null; game: boolean }) {
  // A futures contest's start is its season opener: say what it is instead of "Started 134d ago".
  if (!game) {
    const t = contest?.starts_at ? Date.parse(contest.starts_at) : NaN;
    return <span>{Number.isFinite(t) && t > Date.now() ? `Futures · opens ${fmtKickoff(contest!.starts_at)}` : "Futures & outrights"}</span>;
  }
  if (st?.live) {
    const text = [st.period, st.clock].filter(Boolean).join(" · ");
    return (
      <span className="inline-flex items-center gap-1.5 font-medium text-blue">
        <span className="pulse inline-block h-2 w-2 rounded-full bg-blue" aria-hidden />
        <span className="nums">Live{text ? ` · ${text}` : ""}</span>
      </span>
    );
  }
  if (st?.final) return <span className="font-medium text-dim">Final</span>;
  return <span className="nums">{startLine(contest)}</span>;
}

/** No game-state scope: the start time plus a way to get live state. */
function LiveStateGate() {
  const access = useAccess(SCOPES.gamestate);
  if (access.ok) return null;
  if (access.reason === "signed_out") {
    return (
      <span className="inline-flex items-center gap-2 text-[12px] text-faint">
        Live score
        <SignInButton size="sm" label="Sign in" />
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={() => showUnlock(SCOPES.gamestate)}
      className="inline-flex min-h-11 items-center gap-1.5 rounded-full border border-border-2 px-3 text-[12px] text-dim hover:border-amber/50 hover:text-amber lg:min-h-8"
    >
      Unlock live score
    </button>
  );
}

/**
 * The slim hero (Revision 1): one row of ~64–72px. Teams (logos, names, score) · league · venues
 * · start time or live clock · live-state gate, then the signals/edge chip and freshness dot at
 * the end. It wraps onto a second line at narrow widths rather than truncating.
 */
export function GameHero({
  contest,
  league,
  state,
  venues,
  logos,
  freshness,
  chip,
}: {
  contest: Contest | undefined;
  league?: League;
  state: HeroState | null;
  venues: number;
  /** Team name (lowercased) → logo URL, from the positions' participant images. */
  logos: Map<string, string>;
  freshness: { ageSec: number | null; wsDown: boolean; hasData: boolean };
  chip: HeroChip | null;
}) {
  const teams = contest ? teamsOf(contest) : null;
  const logoOf = (t: Team) => logos.get((t.name ?? "").toLowerCase()) ?? logos.get((t.abbr ?? "").toLowerCase()) ?? null;
  const scored = hasScore(state);
  const lead = scored && state!.home != null && state!.away != null ? Math.sign(state!.home - state!.away) : 0;
  const sep = contest ? matchupSep(contest) : "@";

  return (
    <SoftCard highlight={!!state?.live} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
      {teams ? (
        <h1 className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
          <TeamChip team={teams.away} logo={logoOf(teams.away)} score={scored ? (state!.away ?? 0) : null} bold={lead <= 0} />
          <span className="text-[14px] text-faint">{sep}</span>
          <TeamChip team={teams.home} logo={logoOf(teams.home)} score={scored ? (state!.home ?? 0) : null} bold={lead >= 0} />
        </h1>
      ) : (
        <h1 className="min-w-0 break-words text-[17px] font-semibold leading-snug text-ink">{contest?.title ?? "—"}</h1>
      )}

      <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-dim">
        <span className="inline-flex min-w-0 items-center gap-1.5 text-[12px] text-faint">
          <VenueLogo name={league?.name} logoUrl={league?.image_url} size={16} />
          <span className="font-medium uppercase tracking-wide text-dim">{league?.name ?? "—"}</span>
          {venues > 0 && (
            <span className="whitespace-nowrap">
              · {venues} venue{venues === 1 ? "" : "s"}
            </span>
          )}
        </span>
        <StatusLine contest={contest} st={state} game={!!contest && isGame(contest)} />
        {teams && <LiveStateGate />}
      </div>

      <div className="flex min-w-0 items-center gap-2 lg:ml-auto">
        {chip && (
          <button
            type="button"
            onClick={chip.onClick}
            className="inline-flex min-h-11 min-w-0 max-w-full items-center gap-1.5 rounded-full border border-amber/50 bg-amber/10 px-3 text-left text-[12px] font-medium text-amber hover:bg-amber/20 lg:min-h-8"
          >
            <span aria-hidden>⚡</span>
            <span className="min-w-0 truncate">{chip.text}</span>
          </button>
        )}
        {freshness.hasData && <FreshnessDot ageSec={freshness.ageSec} wsDown={freshness.wsDown} />}
      </div>
    </SoftCard>
  );
}

/** The one-line hero the mobile sticky header shows once the hero has scrolled away. */
export function CompactHero({ contest, state }: { contest: Contest | undefined; state: HeroState | null }) {
  const teams = contest ? teamsOf(contest) : null;
  const short = (t: Team) => t.abbr ?? t.name ?? "—";
  let right: string;
  if (state?.live) right = [state.period, state.clock].filter(Boolean).join(" · ") || "Live";
  else if (state?.final) right = "Final";
  else right = contest?.starts_at ? fmtKickoff(contest.starts_at) : "";
  return (
    <div className="gb-fade flex min-h-10 min-w-0 items-center gap-2 px-4 text-[13px]">
      <span className="min-w-0 flex-1 truncate font-semibold text-ink">
        {teams ? (
          hasScore(state) ? (
            <span className="nums">
              {short(teams.away)} {state!.away ?? 0} – {state!.home ?? 0} {short(teams.home)}
            </span>
          ) : (
            `${short(teams.away)} @ ${short(teams.home)}`
          )
        ) : (
          (contest?.title ?? "")
        )}
      </span>
      <span className={`nums shrink-0 text-[12px] ${state?.live ? "text-blue" : "text-dim"}`}>{right}</span>
    </div>
  );
}

export function HeroSkeleton() {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-border bg-panel-2 px-4 py-3" aria-hidden>
      <div className="flex min-h-9 items-center gap-2">
        <Skeleton className="h-6 w-6 rounded" />
        <Skeleton className="h-5 w-28" />
        <Skeleton className="h-6 w-6 rounded" />
        <Skeleton className="h-5 w-28" />
      </div>
      <Skeleton className="h-4 w-52 max-w-full" />
    </div>
  );
}
